import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api, fileUrl } from "../api";
import { useLang } from "../i18n";
import { Reveal, SectionHead } from "./Section";

const ArtistCard = ({ a, i }) => (
    <Reveal delay={i * 0.08}>
        <div data-testid={`artist-card-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="group relative rounded-3xl overflow-hidden border border-lakou-gold/15 bg-lakou-coal/60 hover:border-lakou-gold/45 transition-colors duration-500">
            <div className="aspect-[4/5] overflow-hidden">
                <img src={fileUrl(a.photo_path)} alt={a.name} loading="lazy"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-lakou-void via-lakou-void/60 to-transparent p-5 pt-16">
                <div className="font-display font-extrabold text-xl text-lakou-cream">{a.name}</div>
                <div className="mt-0.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-lakou-gold">{a.genre}</div>
            </div>
        </div>
    </Reveal>
);

export const Lineup = () => {
    const { t } = useLang();
    const [artists, setArtists] = useState([]);

    useEffect(() => {
        api.get("/artists").then((r) => setArtists(r.data.artists || [])).catch(() => {});
    }, []);

    if (!artists.length) return null;
    const special = artists.find((a) => a.special);
    const rest = artists.filter((a) => !a.special);

    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32" data-testid="lineup-section">
            <SectionHead eyebrow={t("lineup_eyebrow")} title={t("lineup_title")} testId="lineup-title" />

            {special && (
                <Reveal delay={0.05}>
                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 rounded-3xl overflow-hidden border border-lakou-gold/40 bg-lakou-coal/70 tracer-border" data-testid="special-guest-card">
                        <div className="aspect-[4/5] lg:aspect-auto lg:max-h-[560px] overflow-hidden">
                            <img src={fileUrl(special.photo_path)} alt={special.name} loading="lazy"
                                className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="p-8 sm:p-12 flex flex-col justify-center">
                            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-lakou-gold/15 border border-lakou-gold/40 px-4 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-gold" data-testid="special-guest-badge">
                                <Star size={12} /> {t("special_guest")}
                            </span>
                            <h3 className="mt-5 font-display font-black text-3xl sm:text-5xl text-goldgrad tracking-tight">{special.name}</h3>
                            <p className="mt-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-lakou-sand">{special.genre}</p>
                            <p className="mt-5 text-sm sm:text-base text-lakou-grey leading-relaxed">{special.bio}</p>
                        </div>
                    </div>
                </Reveal>
            )}

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((a, i) => <ArtistCard key={a.id} a={a} i={i} />)}
            </div>
        </section>
    );
};
